import { useEffect } from 'react';

interface AdSenseProps {
  adSlot: string;
  adFormat?: string;
  fullWidthResponsive?: boolean;
  style?: React.CSSProperties;
}

declare global {
  interface Window {
    adsbygoogle?: any[];
  }
}

// Placeholder ad unit IDs copied from Google's own AdSense documentation
// examples — never real ad units. Rendering them just reserves blank space
// with zero chance of ever filling, so we skip the slot entirely until it's
// swapped for a real ID created in the AdSense dashboard.
const PLACEHOLDER_SLOT_IDS = new Set(['1234567890', '9876543210']);

export const AdSense: React.FC<AdSenseProps> = ({
  adSlot,
  adFormat = 'auto',
  fullWidthResponsive = true,
  style = { display: 'block' },
}) => {
  const ADSENSE_CLIENT_ID = import.meta.env.VITE_ADSENSE_CLIENT_ID;
  const isPlaceholderSlot = PLACEHOLDER_SLOT_IDS.has(adSlot);

  useEffect(() => {
    if (!ADSENSE_CLIENT_ID || isPlaceholderSlot) return;

    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
    } catch (err) {
      console.error('AdSense error:', err);
    }
  }, [ADSENSE_CLIENT_ID, isPlaceholderSlot]);

  if (!ADSENSE_CLIENT_ID || isPlaceholderSlot) {
    return null;
  }

  return (
    <ins
      className="adsbygoogle"
      style={style}
      data-ad-client={ADSENSE_CLIENT_ID}
      data-ad-slot={adSlot}
      data-ad-format={adFormat}
      data-full-width-responsive={fullWidthResponsive.toString()}
    />
  );
};

// Component for loading AdSense script
export const AdSenseScript: React.FC = () => {
  const ADSENSE_CLIENT_ID = import.meta.env.VITE_ADSENSE_CLIENT_ID;

  useEffect(() => {
    if (!ADSENSE_CLIENT_ID) return;

    const script = document.createElement('script');
    script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${ADSENSE_CLIENT_ID}`;
    script.async = true;
    script.crossOrigin = 'anonymous';
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, [ADSENSE_CLIENT_ID]);

  return null;
};
